import axios, { CancelTokenSource } from 'axios';
import React from 'react';
import { connect } from 'react-redux';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Dropzone from 'react-dropzone';

import { IApplicationState } from '../../store/applicationState';
import { IGroup, IGroupMember, IMember } from '../../store/group/groupState';
import { fetchGroup, fetchGroupMembers } from '../../store/group/groupActions';
import { updateGroup, setGroupResponsible } from '../../services/group';
import { getProfileByInitials } from '../../services/person';

import Contact from '../../components/shared/contact/Contact';
import FormOption from '../../components/forms/formOption/FormOption';
import FormSelect from '../../components/forms/formSelect/FormSelect';
import FormText from '../../components/forms/formText/FormText';
import FormTextArea from '../../components/forms/formTextarea/FormTextarea';
import Icon from '../../components/shared/icon/Icon';
import LinkButton from '../../components/forms/linkButton/LinkButton';
import Modal from '../../components/shared/modal/Modal';
import Spinner from '../../components/shared/spinner/Spinner';
import Arrow from '../../components/shared/arrow/Arrow';
import Button from '../../components/forms/button/Button'
import Tags from '../../components/shared/tags/Tags';
import AttachmentIcon from '../../components/shared/attachmentlink/AttachmentIcon';
import PracticalContact from './PracticalContact';

import getConfiguration from '../../services/environment';
import blobToBase64Url from '../../services/blobToBase64Url';
import resizeImage from '../../services/resizeImage';

interface IOwnProps {
    group?: IGroup;
    groupMembers?: IGroupMember[];
}

interface IOwnState {
    isExpanded: boolean;
    showEditDescription: boolean;
    showIntroMessageSample: boolean;
    groupDescription: string;
    isSavingDescription: boolean;
    showEditEmail: boolean;
    groupEmail: string;
    isSavingAlias: boolean;
    showAddResponsible: boolean;
    responsibleInitials: string;
    responsibleRole: string;
    isSearchingResponsible: boolean;
    foundContact: IMember | null;
    canSaveResponsible: boolean;
    isSavingResponsible: boolean;
    cancelToken: CancelTokenSource;
    emailDomain: string;
    showMembersInfo: string;
    groupImageDataUri: string;
    isGroupImageUploading: boolean;
    isGroupImageDeleting: boolean;
}

interface IStateProps {
    viewerIsDi: boolean;
}

interface IDispatchProps {
    fetchGroup: typeof fetchGroup;
    fetchGroupMembers: typeof fetchGroupMembers;
}

type Props = IOwnProps & IStateProps & IDispatchProps;

const getProfileByInitialsDebounced = AwesomeDebouncePromise(getProfileByInitials, 500);
const activeStyle = { backgroundColor: 'rgb(238, 238, 238)', borderColor: 'rgb(102, 204, 102)' };
const rejectStyle = { borderColor: 'red' };

class Practical extends React.Component<Props, IOwnState> {
    private dropzone = React.createRef<Dropzone>();

    constructor(props: Props) {
        super(props);

        let smi = 'none';

        if (this.props.group) {
            if (this.props.group.showMembersInfo) {
                smi = this.props.group.showMembersInfo;
            } else if (this.props.group.showMembers) {
                smi = 'all';
            }
        }

        this.state = {
            isExpanded: false,
            showEditDescription: false,
            showIntroMessageSample: false,
            groupDescription: '',
            isSavingDescription: false,
            showEditEmail: false,
            groupEmail: '',
            isSavingAlias: false,
            showAddResponsible: false,
            responsibleInitials: '',
            responsibleRole: '',
            isSearchingResponsible: false,
            foundContact: null,
            canSaveResponsible: false,
            isSavingResponsible: false,
            cancelToken: axios.CancelToken.source(),
            emailDomain: '',
            showMembersInfo: smi,
            groupImageDataUri: '',
            isGroupImageUploading: false,
            isGroupImageDeleting: false
        };
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.group !== this.props.group) {
            let smi = 'none';

            if (this.props.group) {
                if (this.props.group.showMembersInfo) {
                    smi = this.props.group.showMembersInfo;
                } else if (this.props.group.showMembers) {
                    smi = 'all';
                }

                this.setState({
                    showMembersInfo: smi
                });
            }
        }
    }

    public async componentDidMount() {
        const config = await getConfiguration();

        this.setState({ emailDomain: config.emailDomain });
    }

    public render() {
        if (!this.props.group) { return null };

        const responsibles = this.props.groupMembers ? this.props.groupMembers.filter(s => s.role === 'Responsible') : [];
        const coordinators = this.props.groupMembers ? this.props.groupMembers.filter(s => s.role === 'Coordinator') : [];
        const minifiedResponsibles = coordinators.length > 0 ? coordinators : responsibles;
        const hasContacts = responsibles.length > 0 || coordinators.length > 0;
        const hasDescription = this.props.group.description;

        return <React.Fragment>
            <section className="section">
                <div className="deck">
                    <div className="w--md">
                        <div className={'practical'.cAppend(this.state.isExpanded, 'is-active')}>
                            <div className="practical__column pointer" onClick={this.togglePractical}>
                                <h1 className="practical__headline">Praktisk info om gruppen</h1>
                                {this.props.viewerIsDi ?
                                    <a href="/#" className="practical__toggle" onClick={(e) => e.preventDefault()}>
                                        <span className="button button--action has-icon">
                                            <span className="text">
                                                <span className="icon icon--right-spacing">
                                                    <Icon name="edit" />
                                                </span>
                                                {this.state.isExpanded ? 'Luk rediger' : 'Rediger'}
                                            </span>
                                        </span>
                                    </a>
                                    :
                                    <a href="/#" className="practical__toggle" onClick={(e) => e.preventDefault()}>
                                        {this.state.isExpanded ? 'Læs mindre' : 'Læs mere'}
                                    </a>
                                }
                            </div>

                            {!this.state.isExpanded && minifiedResponsibles.length > 0 &&
                                minifiedResponsibles.map((responsible, index) => {
                                    return <div className="practical__column practical__contact-minified" key={index}>
                                        <Contact member={responsible} modifierClass="contact--practical" useRoundedImage={true} useRoleTitle={true} hideContactInfo={true} />
                                    </div>;
                                })
                            }

                            {this.state.isExpanded &&
                                <div className={'practical__expanded'.cAppend(!hasContacts && !this.props.viewerIsDi, 'practical__description--full-width')}>
                                    <div className="practical__description">
                                        {(hasDescription || this.props.viewerIsDi) &&
                                            <React.Fragment>
                                                <h2 className="practical__headline">Introduktion til gruppen</h2>
                                                <div className="article article--no-initial rich-text">
                                                    <p style={{ marginBottom: 0 }}>{hasDescription ? this.props.group.description!.split('\n').map((item, key) => {
                                                        return <React.Fragment key={key}>{item}<br /></React.Fragment>
                                                    }) : 'Der er endnu ikke skrevet en introduktion.'}</p>

                                                    {this.props.viewerIsDi &&
                                                        <p>
                                                            <a href="/#" className="button button--action has-icon" onClick={this.toggleEditDescription}>
                                                                <span className="text">
                                                                    <span className="icon icon--right-spacing">
                                                                        <Icon name="edit" />
                                                                    </span>
                                                                    Rediger introduktion til gruppen
                                                                </span>
                                                            </a>
                                                        </p>
                                                    }
                                                </div>
                                            </React.Fragment>
                                        }

                                        {(this.props.viewerIsDi) &&
                                            <React.Fragment>
                                                <h2 className="practical__headline">Velkomstbesked til nye deltagere</h2>

                                                Der sendes {(!this.props.group.sendIntroMessage) && <React.Fragment>ikke </React.Fragment>}velkomstbesked for gruppen. <a href="/#" className="button button--action" onClick={this.toggleIntroMessageSample}>Se eksempel</a><br />
                                                {(!this.props.group.sendIntroMessage) &&
                                                    <React.Fragment>
                                                        <a href="/#" className="button button--action" onClick={this.activateIntroMessage}>Aktiver velkomstbesked</a> <br />
                                                        <a href="/#" className="button button--action" onClick={this.activateIntroMessageForCurrent}>Aktiver velkomstbesked og send også til nuværende deltagere</a> <br />
                                                    </React.Fragment>
                                                }
                                                {(this.props.group.sendIntroMessage) &&
                                                    <a href="/#" className="button button--action" onClick={this.deactivateIntroMessage}>Deaktiver velkomstbesked</a>
                                                }
                                            </React.Fragment>
                                        }

                                        {(this.props.group.canSendEmail === "all" || this.props.viewerIsDi) &&
                                            <React.Fragment>
                                                <h2 className="practical__headline">Gruppens e-mail</h2>
                                                {this.state.emailDomain && this.props.group && this.props.group.enabled && <div className="article article--no-initial rich-text">
                                                    <p>Skriv til denne adresse for at sende til alle deltagere i gruppen:<br />
                                                        <a href={`mailto:${this.props.group.alias}@${this.state.emailDomain}`} className="button button--action has-icon">
                                                            <span className="text">
                                                                <span className="icon icon--right-spacing">
                                                                    <Icon name="share-email" />
                                                                </span>
                                                                {this.props.group.alias}@{this.state.emailDomain}
                                                            </span>
                                                        </a>
                                                    </p>
                                                    {(this.props.viewerIsDi) &&
                                                        <React.Fragment>
                                                            <p>
                                                                <input type="radio" name="canSendEmail" value="all" checked={this.props.group.canSendEmail === "all"} onChange={this.onCanSendEmailChanged} /> Alle medlemmer kan sende til fælles e-mail<br />
                                                                <input type="radio" name="canSendEmail" value="onlyDI" checked={this.props.group.canSendEmail === "onlyDI"} onChange={this.onCanSendEmailChanged} /> Kun DI afsendere kan sende til fælles e-mail<br />
                                                            </p>
                                                            <p>
                                                                <a href="/#" className="button button--action has-icon" onClick={this.toggleEditEmail}>
                                                                    <span className="text">
                                                                        <span className="icon icon--right-spacing">
                                                                            <Icon name="edit" />
                                                                        </span>
                                                                        Rediger e-mail adresse
                                                                    </span>
                                                                </a>
                                                            </p>
                                                        </React.Fragment>
                                                    }
                                                </div>}
                                            </React.Fragment>
                                        }

                                        {this.props.group && !this.props.group.enabled &&
                                            <div className="article article--no-initial rich-text">
                                                <p>
                                                    Der kan ikke sendes e-mail til denne gruppe.
                                                    {this.props.group.id.substring(0, 2) === 'ud'
                                                        ? ' Aktiver "Vis på web" på netværk/udvalg i Membercare for at aktivere e-mail til gruppen.'
                                                        : this.props.group.id.substring(0, 2) === 'ab'
                                                            ? ' Sæt kategori "Netværk (...)" for abonnement definitionen i Membercare for at aktivere e-mail til gruppen.'
                                                            : ' Aktiver "Vis på web" på arrangementet i Membercare for at aktivere e-mail til gruppen.'
                                                    }
                                                </p>
                                            </div>
                                        }

                                        {this.props.viewerIsDi && this.props.group && this.props.group.enabled &&
                                            <React.Fragment>
                                                <h2 className="practical__headline">Deltagerliste</h2>
                                                <input type="radio" name="participantList" value="none" checked={this.state.showMembersInfo === "none"} onChange={this.saveShowMembers} /> Vis ikke deltagerliste<br />
                                                <input type="radio" name="participantList" value="withoutcontacts" checked={this.state.showMembersInfo === "withoutcontacts"} onChange={this.saveShowMembers} /> Vis deltagerliste uden kontaktoplysninger<br />
                                                <input type="radio" name="participantList" value="all" checked={this.state.showMembersInfo === "all"} onChange={this.saveShowMembers} /> Vis deltagerliste med alle oplysninger<br />

                                                <h2 className="practical__headline">Gruppebillede</h2>
                                                <div className={'form__field form__field--files'}>
                                                    <label className="form__label">
                                                        <span className="form__label__text">Vælg et billede til gruppen som vises øverst til venstre på gruppens side og i gruppe mails</span>
                                                    </label>
                                                    <div className="form__files__container">
                                                        <Dropzone className="form__files__dragndrop" multiple={false} accept="image/*" activeStyle={activeStyle} rejectStyle={rejectStyle} preventDropOnDocument={true} onDrop={this.onDropGroupImage} disabled={this.state.isGroupImageUploading || this.state.isGroupImageDeleting} ref={this.dropzone}>
                                                            <p className="bodytext"><Arrow /> <span className="form__files__dragndrop__highlighted">Gennemse</span> eller slip din fil her.</p>
                                                        </Dropzone>
                                                        {this.state.groupImageDataUri &&
                                                            <div className="form__files__list">
                                                                <Tags alwaysVisible={true} useOverflow={true}>
                                                                    <li className="tags__list__item">
                                                                        <a href="/#" className="button button--tag" onClick={this.onCancelGroupImage}>
                                                                            <Icon name="trash" />
                                                                            <AttachmentIcon mimeType='image/jpeg' />
                                                                            Billede
                                                                        </a>
                                                                    </li>
                                                                </Tags>
                                                                <hr className="spacing spacing--xsmall" />
                                                                <div className="flex-grid">
                                                                    <div className="flex-grid__col flex-grid__col--12 align-right">
                                                                        <div className="action-buttons">
                                                                            <Button modifierClass="button--small button--border button--inverted" onClick={this.onCancelGroupImage} disabled={this.state.isGroupImageUploading}>Annuller</Button>
                                                                            <Button modifierClass="button--small button--primary" onClick={this.onUploadGroupImage} disabled={this.state.isGroupImageUploading} spinning={this.state.isGroupImageUploading}>Gem</Button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                        {!this.state.groupImageDataUri && this.props.group.icon &&
                                                            <div className="form__files__list">
                                                                <img src={this.props.group.icon} alt="Logo" />
                                                                <hr className="spacing spacing--xsmall" />
                                                                <div className="flex-grid">
                                                                    <div className="flex-grid__col flex-grid__col--12 align-right">
                                                                        <div className="action-buttons">
                                                                            <Button modifierClass="button--small button--border button--inverted" onClick={this.onDeleteGroupImage} disabled={this.state.isGroupImageDeleting} spinning={this.state.isGroupImageDeleting}>Slet</Button>
                                                                            <Button modifierClass="button--small button--primary" onClick={this.onReselectGroupImage} disabled={this.state.isGroupImageDeleting}>Skift gruppebillede</Button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        }
                                    </div>
                                    {(hasContacts || this.props.viewerIsDi) &&
                                        <div className="practical__contacts">
                                            {responsibles.map((responsible, index) => <PracticalContact groupId={this.props.group!.id} member={responsible} key={`responsible${index.toString()}`} />)}
                                            {coordinators.map((coordinator, index) => <PracticalContact groupId={this.props.group!.id} member={coordinator} key={`coordinator${index.toString()}`} />)}
                                            {this.props.viewerIsDi && this.props.group.canEditResponsible &&
                                                <div className="practical__contact">
                                                    <a href="/#" className="button button--action has-icon" onClick={this.toggleAddingResponsible}>
                                                        <span className="text">
                                                            <span className="icon icon--right-spacing">
                                                                <Icon name="plus" />
                                                            </span>
                                                            Tilføj ansvarlig
                                                        </span>
                                                    </a>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </section>

            <Modal show={this.state.showIntroMessageSample} onClose={this.toggleIntroMessageSample}>
                <div className="rich-text">
                    <h3>Introduktions mail</h3>
                </div>

                <div className="rich-text">
                    Kære [deltagernavn]
                    {this.props.group.description && <React.Fragment><br /><br /><span dangerouslySetInnerHTML={{ __html: this.props.group.description.cFormatNewLines() }} /></React.Fragment>}
                    {(this.props.group.id.substring(0, 2) === 'ab' || this.props.group.id.substring(0, 2) === 'ud') && <React.Fragment><br /><br />I gruppen kan du:<br />
                        <ul>
                            <li>Se informationer om de øvrige deltagere</li>
                            <li>Sende beskeder og se tidligere beskeder og dokumenter</li>
                            <li>Se indkaldte møder og hvem der deltager</li>
                        </ul>
                        Gå til gruppen ved at trykke på knappen herunder. Når du besøger gruppen første gang har du mulighed for at lægge et foto ind og skrive mere om dig selv.
                    </React.Fragment>}
                    <br /><br />[signatur for ansvarlige]
                </div>
            </Modal>

            <Modal show={this.state.showEditDescription} onClose={this.toggleEditDescription}>
                <div className="rich-text">
                    <h3>Introduktion til gruppen</h3>
                </div>

                <div className="modal__content__form">
                    <FormTextArea name="introduction" label="Introduktion" value={this.state.groupDescription} onChange={this.handleDescriptionChange} />
                </div>

                <div className="modal__content__form__actions modal__content__form__actions--border">
                    <div className="flex-grid">
                        <div className="flex-grid__col flex-grid__col--12 flex-grid__col--sm-12 flex-grid__col--lg-12 align-right form__section__inner">
                            <LinkButton modifierClass="button--discreet button--light" disabled={this.state.isSavingDescription} onClick={this.toggleEditDescription}>Annuller</LinkButton>
                            <LinkButton modifierClass="button--primary button--primary button--large" spinning={this.state.isSavingDescription} onClick={this.saveDescription}>Gem</LinkButton>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal show={this.state.showEditEmail} onClose={this.toggleEditEmail}>
                <div className="rich-text">
                    <h3>Rediger e-mail for gruppen</h3>
                </div>

                <div className="modal__content__form form">
                    <div className="flex-grid">
                        <div className="flex-grid__col flex-grid__col--6">
                            <FormText name="email" label="E-mail" initialValue={this.state.groupEmail} onChange={this.handleEmailChange} validator={this.validateAlias} notice="E-mail adresse må kun bestå af bogstaver, tal og underscore (_). Adressen må ikke starte med et tal og må ikke starte eller slutte med en underscore." />
                        </div>
                        <div className="flex-grid__col flex-grid__col--6">
                            <FormText name="domain" label="" modifierClass="is-incognito" initialValue={`@${this.state.emailDomain}`} disabled={true} />
                        </div>
                    </div>
                </div>

                <div className="modal__content__form__actions modal__content__form__actions--border">
                    <div className="flex-grid">
                        <div className="flex-grid__col flex-grid__col--12 flex-grid__col--sm-12 flex-grid__col--lg-12 align-right form__section__inner">
                            <LinkButton modifierClass="button--discreet button--light" disabled={this.state.isSavingAlias} onClick={this.toggleEditEmail}>Annuller</LinkButton>
                            <LinkButton modifierClass="button--primary button--primary button--large" spinning={this.state.isSavingAlias} onClick={this.saveAlias}>Gem</LinkButton>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal show={this.state.showAddResponsible} onClose={this.toggleAddingResponsible}>
                <div className="rich-text">
                    <h3>Tilføj ansvarlig</h3>
                </div>

                <div className="modal__content__form form">
                    <div className="form__section">
                        <FormText name="initials" label="Initialer" placeholder="Skriv initialer på ny ansvarlig" initialValue={''} onChange={this.handleResponsibleInitialsChange} />

                        {this.state.isSearchingResponsible && <Spinner isFormSpinner={true} />}

                        {this.state.foundContact && this.state.foundContact.fullName &&
                            <React.Fragment>
                                <FormText name="name" label="Navn" disabled={true} initialValue={this.state.foundContact!.fullName} />

                                <FormSelect name="role" label="Rolle" onChange={this.handleResponsibleRoleChange} value={this.state.responsibleRole} required={true}>
                                    <FormOption value="">- Vælg</FormOption>
                                    <FormOption value="Coordinator">Faglig ansvarlig</FormOption>
                                    <FormOption value="Responsible">Praktisk ansvarlig</FormOption>
                                </FormSelect>
                            </React.Fragment>
                        }
                    </div>
                </div>

                <div className="modal__content__form__actions modal__content__form__actions--border">
                    <div className="flex-grid">
                        <div className="flex-grid__col flex-grid__col--12 flex-grid__col--sm-12 flex-grid__col--lg-12 align-right form__section__inner">
                            <LinkButton modifierClass="button--discreet button--light" onClick={this.toggleAddingResponsible}>Annuller</LinkButton>
                            <LinkButton modifierClass="button--primary button--primary button--large" disabled={!this.state.canSaveResponsible} spinning={this.state.isSavingResponsible} onClick={this.saveResponsible}>Gem</LinkButton>
                        </div>
                    </div>
                </div>
            </Modal>
        </React.Fragment >;
    }

    private togglePractical = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        this.setState(prevState => ({
            isExpanded: !prevState.isExpanded
        }));
    }

    private toggleEditDescription = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        if (!this.state.isSavingDescription) {
            this.setState(prevState => ({
                showEditDescription: !prevState.showEditDescription,
                groupDescription: this.props.group && this.props.group.description ? this.props.group.description : ''
            }));
        }
    }

    private toggleIntroMessageSample = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        this.setState(prevState => ({
            showIntroMessageSample: !prevState.showIntroMessageSample
        }));

        //Send request to set state on existing users?
    }

    private activateIntroMessageForCurrent = async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();
        if (this.props.group) {
            await updateGroup(this.props.group.id, { sendIntroMessage: 'activateForCurrent' });

            this.props.fetchGroup(this.props.group.id);
        }
    }

    private activateIntroMessage = async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        if (this.props.group) {
            await updateGroup(this.props.group.id, { sendIntroMessage: 'activate' });

            this.props.fetchGroup(this.props.group.id);
        }
    }

    private deactivateIntroMessage = async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        if (this.props.group) {
            await updateGroup(this.props.group.id, { sendIntroMessage: 'deactivate' });

            this.props.fetchGroup(this.props.group.id);
        }
    }

    private handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ groupDescription: e.target.value });
    }

    private saveDescription = async (e: React.FormEvent<HTMLAnchorElement> | React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        if (this.props.group && !this.state.isSavingDescription) {
            this.setState({
                isSavingDescription: true
            });

            // TODO - VALIDATE
            await updateGroup(this.props.group.id, { description: this.state.groupDescription });

            this.props.fetchGroup(this.props.group.id);

            this.setState({
                isSavingDescription: false,
                showEditDescription: false
            });
        }
    }

    private toggleEditEmail = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        if (!this.state.isSavingAlias) {
            this.setState(prevState => ({
                showEditEmail: !prevState.showEditEmail,
                groupEmail: this.props.group && this.props.group.alias ? this.props.group.alias : ''
            }));
        }
    }

    private handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ groupEmail: e.target.value });
    }

    private validateAlias = (alias: string) => {
        return !!alias && alias.length < 100
            && !/(^(([\d_])|((ab|ar|ud)\d))|_$)/.test(alias)
            && /^[0-9a-z_]+$/.test(alias);
    }

    private saveAlias = async (e: React.FormEvent<HTMLAnchorElement> | React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        if (this.props.group && !this.state.isSavingAlias && this.validateAlias(this.state.groupEmail)) {
            this.setState({
                isSavingAlias: true
            });

            await updateGroup(this.props.group!.id, { alias: this.state.groupEmail });
            window.location.href = `/Group/${this.state.groupEmail}`; // on success redirect to new alias forcing reload of user-groups et al
        }
    }

    private toggleAddingResponsible = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        this.setState(prevState => ({
            showAddResponsible: !prevState.showAddResponsible
        }));
    }

    private handleResponsibleInitialsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const initials = e.target.value;

        this.setState({
            responsibleInitials: initials,
            responsibleRole: '',
            foundContact: null,
            canSaveResponsible: false
        });

        if (initials.length > 1) {
            this.setState({ isSearchingResponsible: true });
            this.setState({
                isSearchingResponsible: false,
                foundContact: (await getProfileByInitialsDebounced(initials, this.state.cancelToken)) as IMember
            });
        }
    }

    private handleResponsibleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const role = e.target.value;

        this.setState({ responsibleRole: role, canSaveResponsible: !!role });
    }

    private saveResponsible = async (e: React.FormEvent<HTMLAnchorElement> | React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        if (this.props.group && !this.state.isSearchingResponsible && !this.state.isSavingResponsible && this.state.foundContact) {
            this.setState({
                isSavingResponsible: true
            });

            // TODO - VALIDATE
            await setGroupResponsible(this.props.group.id, { personId: this.state.foundContact!.diNumber, role: this.state.responsibleRole });

            await this.props.fetchGroupMembers(this.props.group.id);

            this.setState({
                isSearchingResponsible: false,
                showAddResponsible: false,
                isSavingResponsible: false,
                responsibleRole: '',
                canSaveResponsible: false,
                foundContact: null
            });
        }
    }

    private saveShowMembers = async (e: React.ChangeEvent<HTMLInputElement>) => {
        let showMembersInfo = e.currentTarget.value;

        if (this.props.group) {
            this.setState(prevState => ({
                showMembersInfo: showMembersInfo//!prevState.showMembers
            }), async () => {
                await updateGroup(this.props.group!.id, { showMembersInfo: showMembersInfo });

                const groupTask = this.props.fetchGroup(this.props.group!.id);
                const membersTask = this.props.fetchGroupMembers(this.props.group!.id);

                await Promise.all([groupTask, membersTask]);
            });
        }
    }

    private onCanSendEmailChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.group) {
            await updateGroup(this.props.group!.id, { canSendEmail: e.currentTarget.value });

            this.props.fetchGroup(this.props.group.id);
        }
    }

    private onDropGroupImage = async (acceptedFiles: any[], rejectedFiles: any[]) => {
        if (acceptedFiles.length !== 1 || rejectedFiles.length > 0) {
            return;
        }

        const base64 = await blobToBase64Url(acceptedFiles[0]);
        const resizedDataUri = await resizeImage(base64, 240, 40);

        this.setState({
            groupImageDataUri: resizedDataUri
        });
    }

    private onCancelGroupImage = (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        e.preventDefault();

        this.setState({
            groupImageDataUri: ''
        });
    }

    private onUploadGroupImage = async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        this.setState({
            isGroupImageUploading: true
        });

        await updateGroup(this.props.group!.id, { dropzoneImageDataUri: this.state.groupImageDataUri });

        await this.props.fetchGroup(this.props.group!.id);

        this.setState({
            groupImageDataUri: '',
            isGroupImageUploading: false
        });
    }

    private onDeleteGroupImage = async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        this.setState({
            isGroupImageDeleting: true
        });

        await updateGroup(this.props.group!.id, { dropzoneImageDataUri: '' });

        await this.props.fetchGroup(this.props.group!.id);

        this.setState({
            isGroupImageDeleting: false
        });
    }

    private onReselectGroupImage = async (e: React.FormEvent<HTMLElement> | React.MouseEvent<HTMLElement> | KeyboardEvent) => {
        this.dropzone.current!.open();
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => ({
    viewerIsDi: state.oidc.user && state.oidc.user.profile ? state.oidc.user.profile.role === 'DI' : false
});

const mapDispatchToProps = (dispatch: any): IDispatchProps => ({
    fetchGroup: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroup(groupId, cancelToken)),
    fetchGroupMembers: (groupId: string, cancelToken: CancelTokenSource = axios.CancelToken.source()) => dispatch(fetchGroupMembers(groupId, cancelToken))
});

const container = connect(mapStateToProps, mapDispatchToProps)(Practical);

export default container;